/**
 * «Контроль качества» (`/qc`): ченджлог базы знаний по замечаниям контролёров и все замечания.
 * `?tab=changelog` — сборки новыми сверху, сборка раскрывается внутри строки (`?changelog=`),
 * пункты сгруппированы по теме карточки (`?item=` — в фокусе); `?tab=remarks` — замечания;
 * `?card=` — карточка базы знаний с основаниями: какие замечания породили каждую версию.
 * Спека `docs/specs/2026-10-25-qc-remarks-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronDown, ChevronRight, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { NoAccess } from "@/components/NoAccess";
import { ChangelogItem, CommentLine } from "@/components/qc/ChangelogItem";
import { RemarkCard, when } from "@/components/qc/RemarkCard";
import { useVoice, VoiceControl, voiceBusy } from "@/components/voice/VoiceInput";
import { CLIENT_CHATS, KNOWLEDGE_EDIT, QC_REMARK, useCan } from "@/lib/access";
import {
  approveSelected,
  commentChangelog,
  loadCardBases,
  loadChangelog,
  loadChangelogs,
  loadRemarks,
  loadSummary,
  MARKS,
  type QcChangelogRow,
  type QcItem,
  reworkAll,
  requestBuild,
  uploadAudio,
} from "@/lib/qc";

const STATUS: Record<string, string> = {
  queued: "в очереди",
  building: "модель разбирает",
  review: "ждёт решений",
  done: "решено",
  empty: "замечаний не было",
  failed: "ошибка",
};

export function QcPage() {
  const editor = useCan(KNOWLEDGE_EDIT);
  const remarker = useCan(QC_REMARK);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? (editor && !params.get("card") ? "changelog" : "remarks");
  const card = Number(params.get("card")) || null;
  if (!editor && !remarker) return <NoAccess what="контролю качества" />;
  const set = (patch: Record<string, string | null>) =>
    setParams((had) => {
      const p = new URLSearchParams(had);
      for (const [k, v] of Object.entries(patch)) {
        if (v) p.set(k, v);
        else p.delete(k);
      }
      return p;
    });
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 [&>*]:flex-none max-md:p-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-annot="шапка контроля качества">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Контроль качества</h1>
        <div role="tablist" aria-label="Раздел" className="flex flex-wrap gap-2">
          {[
            ...(editor ? [{ id: "changelog", label: "Ченджлог", hint: "что модель предлагает изменить в базе знаний" }] : []),
            { id: "remarks", label: "Замечания", hint: "что оставили контролёры в «Чатах»" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => set({ tab: t.id, changelog: null, item: null })}
              className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-4 py-2 ${
                tab === t.id ? "border-accent bg-accent-dim text-accent-text" : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
              }`}
            >
              <span className="text-[15px] font-semibold">{t.label}</span>
              <span className="text-[12px] opacity-80 max-md:hidden">{t.hint}</span>
            </button>
          ))}
        </div>
      </div>
      <SummaryLine editor={editor} />
      {tab === "changelog" && editor ? (
        <Changelogs open={Number(params.get("changelog")) || null} focus={Number(params.get("item")) || null} set={set} />
      ) : (
        <Remarks editor={editor} />
      )}
      {card && <CardBases id={card} onClose={() => set({ card: null })} />}
    </main>
  );
}

function SummaryLine({ editor }: { editor: boolean }) {
  const client = useQueryClient();
  const s = useQuery({ queryKey: ["qc-summary"], queryFn: loadSummary, refetchInterval: 15_000 });
  const build = useMutation({
    mutationFn: requestBuild,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["qc-summary"] });
      void client.invalidateQueries({ queryKey: ["qc-changelogs"] });
    },
  });
  const d = s.data;
  if (!d) return null;
  const next = d.next_auto ? new Date(d.next_auto) : null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-2.5 text-[13px]" data-annot="состояние очереди">
      <span>
        Ждут разбора <b>{d.new}</b>
      </span>
      {d.waiting_voice > 0 && <span>расшифровываются {d.waiting_voice}</span>}
      {d.voice_failed > 0 && <span className="text-err">не расшифровались {d.voice_failed}</span>}
      <span>
        в ченджлоге <b>{d.in_changelog}</b>
      </span>
      <span>
        ждут решения <b>{d.items_review}</b>
      </span>
      {d.items_rework > 0 && <span>у модели в работе {d.items_rework}</span>}
      {d.items_failed > 0 && <span className="text-err">ошибки {d.items_failed}</span>}
      <span className="text-fg-muted">внесено в базу {d.applied}</span>
      {editor && (
        <span className="ml-auto flex items-center gap-2">
          {d.active ? (
            <span className="text-fg-muted">сборка №{d.active.id}: {STATUS[d.active.status]}…</span>
          ) : (
            <span className="text-fg-muted">
              {next
                ? `сама — в ${next.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`
                : "сама не собирается — только по кнопке"}
            </span>
          )}
          <button
            type="button"
            onClick={() => build.mutate()}
            disabled={build.isPending || !!d.active || d.new === 0}
            title={d.new === 0 ? "Разбирать нечего" : "Модель разберёт все ждущие замечания на ближайшей минуте"}
            className="rounded-full bg-accent px-3 py-1 text-[13px] font-medium text-white disabled:opacity-40"
          >
            Собрать сейчас
          </button>
        </span>
      )}
      {build.isError && <span className="text-err">{build.error.message}</span>}
    </div>
  );
}

function Changelogs({ open, focus, set }: { open: number | null; focus: number | null; set: (p: Record<string, string | null>) => void }) {
  const q = useQuery({ queryKey: ["qc-changelogs"], queryFn: loadChangelogs, refetchInterval: 10_000 });
  if (q.isPending) return <p className="text-[13px] text-fg-muted">Загрузка…</p>;
  if (q.isError) return <p className="text-[13px] text-err">{q.error.message}</p>;
  const rows = q.data.changelogs;
  // Ссылка из замечания на старую сборку, которой нет среди последних 30, — открываем её отдельно.
  const extra = open && !rows.some((r) => r.id === open) ? open : null;
  if (!rows.length && !extra) return <p className="text-[13px] text-fg-muted">Сборок ещё не было: замечания разбираются в 13:00 и 20:00 или по кнопке «Собрать сейчас».</p>;
  return (
    <div className="flex flex-col gap-2">
      {extra && <LinkedChangelog id={extra} focus={focus} onClose={() => set({ changelog: null, item: null })} />}
      {rows.map((r) => (
        <ChangelogRow key={r.id} r={r} open={open === r.id} focus={focus} onToggle={() => set({ changelog: open === r.id ? null : String(r.id), item: null })} />
      ))}
    </div>
  );
}

function LinkedChangelog({ id, focus, onClose }: { id: number; focus: number | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ["qc-changelog", id], queryFn: () => loadChangelog(id) });
  if (q.isError) return <p className="text-[13px] text-err">{q.error.message}</p>;
  if (!q.data) return <p className="text-[13px] text-fg-muted">Загрузка сборки №{id}…</p>;
  return <ChangelogRow r={q.data} open focus={focus} onToggle={onClose} />;
}

function ChangelogRow({ r, open, focus, onToggle }: { r: QcChangelogRow; open: boolean; focus: number | null; onToggle: () => void }) {
  return (
    <section className={`rounded-[var(--radius-card)] border ${open ? "border-accent" : "border-border"}`} data-annot="сборка ченджлога">
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left text-[13.5px] hover:bg-bg-hover">
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <span className="font-medium">Сборка №{r.id}</span>
        <span className="text-fg-muted">
          {when(r.built || r.date)} · {r.trigger === "auto" ? "по расписанию" : "по кнопке"}
        </span>
        <span className={r.status === "failed" ? "text-err" : r.status === "done" ? "text-ok" : ""}>{STATUS[r.status]}</span>
        {r.items > 0 && (
          <span className="rounded-full bg-bg-subtle px-2 py-0.5 text-[12px]">
            пунктов {r.items}
            {r.pending ? ` · ждут ${r.pending}` : ""}
          </span>
        )}
        {r.remarks > 0 && <span className="text-[12px] text-fg-muted">замечаний {r.remarks}</span>}
        {r.missed > 0 && <span className="text-[12px] text-err">не разобрано {r.missed}</span>}
        {r.cost ? <span className="ml-auto text-[12px] text-fg-faint" title="Стоимость плана; редакции и доработки — у пунктов">план ${r.cost.toFixed(3)}</span> : null}
      </button>
      {r.summary && !open && <p className="px-4 pb-2.5 text-[13px] text-fg-muted">{r.summary}</p>}
      {r.error && <p className="px-4 pb-2.5 text-[13px] text-err">{r.error}</p>}
      {open && <ChangelogBody id={r.id} focus={focus} />}
    </section>
  );
}

function ChangelogBody({ id, focus }: { id: number; focus: number | null }) {
  const client = useQueryClient();
  const key = ["qc-changelog", id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => loadChangelog(id),
    refetchInterval: (query) => (query.state.data?.items_list.some((i) => i.status === "drafting" || i.status === "rework") ? 6000 : 30_000),
  });
  // Выбор помнит ревизию, которую редактор видел: пункт доработали — галочка снимается.
  const [picked, setPicked] = useState<Map<number, number>>(new Map());
  const [card, setCard] = useState<number | null>(null);
  const [text, setText] = useState("");
  // Голос уходит на сервер сразу после «Стоп» (`@/lib/voice`); с комментарием — только его токен.
  const [audio, setAudio] = useState<string | null>(null);
  const voice = useVoice({ onDone: async (b, s) => setAudio(await uploadAudio(b, s)) });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: key });
    void client.invalidateQueries({ queryKey: ["qc-changelogs"] });
    void client.invalidateQueries({ queryKey: ["qc-summary"] });
  };
  const items = useMemo(() => q.data?.items_list ?? [], [q.data]);
  const [dropped, setDropped] = useState(0);
  useEffect(() => {
    const ушли = [...picked].filter(([pid, rev]) => items.find((i) => i.id === pid)?.revision !== rev || items.find((i) => i.id === pid)?.status !== "review");
    if (ушли.length) {
      setPicked((had) => {
        const n = new Map(had);
        for (const [pid] of ушли) n.delete(pid);
        return n;
      });
      setDropped((d) => d + ушли.length);
    }
  }, [items, picked]);
  const approve = useMutation({
    mutationFn: () => approveSelected(id, [...picked]),
    onSuccess: () => (setPicked(new Map()), refresh()),
  });
  const [general, setGeneral] = useState(false);
  const generalAct = useMutation({
    mutationFn: async (all: boolean) => {
      const body = { text, audio };
      return all ? reworkAll(id, body) : commentChangelog(id, body);
    },
    onSuccess: () => (setText(""), setAudio(null), voice.reset(), refresh()),
  });
  useEffect(() => {
    if (focus && q.data) document.getElementById(`qc-item-${focus}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [focus, q.data]);
  if (q.isPending) return <p className="px-4 pb-3 text-[13px] text-fg-muted">Загрузка…</p>;
  if (q.isError) return <p className="px-4 pb-3 text-[13px] text-err">{q.error.message}</p>;
  const d = q.data;
  const groups = new Map<string, QcItem[]>();
  for (const i of items) groups.set(i.topic || "прочее", [...(groups.get(i.topic || "прочее") ?? []), i]);
  const reviewable = items.filter((i) => i.status === "review");
  const pickedItems = reviewable.filter((i) => picked.has(i.id));
  const hasInput = text.trim().length > 0 || !!audio;
  return (
    <div className="flex flex-col gap-3 border-t border-border px-4 py-3">
      {d.summary && <p className="text-[13.5px]">{d.summary}</p>}
      {reviewable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <button
            type="button"
            onClick={() => setPicked(picked.size === reviewable.length ? new Map() : new Map(reviewable.map((i) => [i.id, i.revision])))}
            className="rounded-full px-3 py-1 hover:bg-bg-hover"
          >
            {picked.size === reviewable.length ? "Снять выбор" : `Выбрать все ждущие (${reviewable.length})`}
          </button>
          <button
            type="button"
            onClick={() => approve.mutate()}
            disabled={!pickedItems.length || approve.isPending}
            title="Правки и новые статьи уйдут в базу знаний, «база не меняется» — будет согласовано"
            className="rounded-full bg-accent px-3 py-1 font-medium text-white disabled:opacity-40"
          >
            Утвердить выбранные {pickedItems.length || ""}
          </button>
          {pickedItems.length > 0 && (
            <span className="text-fg-muted">
              правок {pickedItems.filter((i) => i.action === "edit").length} · новых статей {pickedItems.filter((i) => i.action === "create").length} · без изменений{" "}
              {pickedItems.filter((i) => i.action === "none").length}
            </span>
          )}
          {dropped > 0 && <span className="text-warn">сняты отметки у доработанных пунктов: {dropped} — посмотрите новую редакцию</span>}
          {approve.data && approve.data.errors.length > 0 && <span className="text-err">не утверждено {approve.data.errors.length}: {approve.data.errors[0]?.error}</span>}
          {approve.data && approve.data.rework.length > 0 && <span className="text-warn">ушли на доработку (карточку уже правили): {approve.data.rework.length}</span>}
        </div>
      )}
      {[...groups.entries()].map(([topic, list]) => (
        <div key={topic} className="flex flex-col gap-2">
          <h3 className="text-[12px] font-medium uppercase tracking-wide text-fg-faint">
            {topic} · {list.length}
          </h3>
          {list.map((i) => (
            <ChangelogItem
              key={i.id}
              item={i}
              picked={picked.has(i.id)}
              onPick={(on) =>
                setPicked((had) => {
                  const n = new Map(had);
                  if (on) n.set(i.id, i.revision);
                  else n.delete(i.id);
                  return n;
                })
              }
              cities={d.cities ?? {}}
              focused={focus === i.id}
              invalidate={key}
              onCard={setCard}
            />
          ))}
        </div>
      ))}
      {!general ? (
        <button type="button" onClick={() => setGeneral(true)} className="self-start text-[13px] text-fg-muted hover:text-fg">
          ▸ Комментарий ко всей сборке{d.comments.length ? ` (${d.comments.length})` : ""} · «Всё на доработку»
        </button>
      ) : (
      <div className="rounded-[12px] border border-dashed border-border px-3 py-2.5" data-annot="комментарий ко всей сборке">
        <div className="mb-1.5 text-[13px] font-medium">Ко всей сборке</div>
        {d.comments.map((c) => (
          <div key={c.id} className="mb-1">
            <CommentLine c={c} />
          </div>
        ))}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Общий комментарий: что учесть во всех пунктах — или голосом"
          className="w-full rounded-[8px] border border-border bg-transparent px-2 py-1.5 text-[13.5px] max-md:text-[16px]"
        />
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <VoiceControl
            voice={voice}
            disabled={generalAct.isPending || !!audio}
            onDrop={audio ? () => setAudio(null) : undefined}
            sentText="Голос загружен — уйдёт с комментарием"
          />
          <div className="ml-auto flex gap-1.5">
            <button type="button" onClick={() => generalAct.mutate(false)} disabled={!hasInput || generalAct.isPending || voiceBusy(voice.v) || !!voice.v.blob} className="rounded-full px-3 py-1.5 text-[13px] font-medium hover:bg-bg-hover disabled:opacity-40">
              Комментарий
            </button>
            <button
              type="button"
              onClick={() => generalAct.mutate(true)}
              disabled={!hasInput || generalAct.isPending || voiceBusy(voice.v) || !!voice.v.blob || !items.some((i) => i.status === "review" || i.status === "failed")}
              title="Все пункты, ждущие решения, модель переделает с учётом комментария"
              className="rounded-full bg-warn-bg px-3 py-1.5 text-[13px] font-medium text-warn disabled:opacity-40"
            >
              Всё на доработку
            </button>
          </div>
        </div>
        {generalAct.isError && <p className="mt-1 text-[12.5px] text-err">{generalAct.error.message}</p>}
      </div>
      )}
      {card && <CardBases id={card} onClose={() => setCard(null)} />}
    </div>
  );
}

function Remarks({ editor }: { editor: boolean }) {
  const chats = useCan(CLIENT_CHATS);
  const [mine, setMine] = useState(!editor);
  const [status, setStatus] = useState("");
  const [mark, setMark] = useState("");
  const [author, setAuthor] = useState<number | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const key = ["qc-remarks-list", mine, status, mark, author, from, to];
  const q = useQuery({ queryKey: key, queryFn: () => loadRemarks({ mine, status, mark, author, from, to }), refetchInterval: 20_000 });
  const sel = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px]";
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2" data-annot="отбор замечаний">
        {(q.data?.all_allowed ?? (editor || chats)) && (
          <div className="flex rounded-full bg-bg-subtle p-0.5 text-[13px]">
            {[
              [true, "Мои"],
              [false, "Все"],
            ].map(([v, label]) => (
              <button key={String(label)} type="button" onClick={() => setMine(v as boolean)} className={`rounded-full px-3 py-0.5 ${mine === v ? "bg-bg font-medium shadow-sm" : "text-fg-muted"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
          <option value="">Все статусы</option>
          <option value="new">Ждут разбора</option>
          <option value="waiting_voice">Расшифровываются</option>
          <option value="voice_failed">Не расшифровались</option>
          <option value="in_changelog">В ченджлоге</option>
          <option value="applied">Внесено в базу</option>
          <option value="no_action">База не меняется</option>
          <option value="rejected">Отклонено</option>
        </select>
        <select value={mark} onChange={(e) => setMark(e.target.value)} className={sel}>
          <option value="">Любая оценка</option>
          {MARKS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
        {!mine && !!q.data?.authors.length && (
          <select value={author ?? ""} onChange={(e) => setAuthor(Number(e.target.value) || null)} className={sel}>
            <option value="">Все контролёры</option>
            {q.data.authors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.count}
              </option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-1 text-[13px] text-fg-muted">
          с <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={sel} />
        </label>
        <label className="flex items-center gap-1 text-[13px] text-fg-muted">
          по <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={sel} />
        </label>
        {q.data && <span className="text-[13px] text-fg-muted">показано {q.data.remarks.length}{q.data.remarks.length >= 300 ? " последних" : ""}</span>}
      </div>
      {q.isError && <p className="text-[13px] text-err">{q.error.message}</p>}
      {q.data?.remarks.length === 0 && <p className="text-[13px] text-fg-muted">Замечаний нет. Оставить — в «Чатах»: значок слева у сообщения или звонка.</p>}
      <div className="grid gap-2 lg:grid-cols-2">
        {q.data?.remarks.map((r) => (
          <RemarkCard key={r.id} r={r} editor={editor} invalidate={key} />
        ))}
      </div>
    </div>
  );
}

/** Карточка базы знаний с блоком «Основания»: версии и замечания, по которым создана каждая. */
function CardBases({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useQuery({ queryKey: ["qc-card", id], queryFn: () => loadCardBases(id) });
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside className="flex h-full w-full max-w-[560px] flex-col bg-bg shadow-2xl" onClick={(e) => e.stopPropagation()} data-annot="основания карточки">
        <div className="flex h-11 flex-none items-center gap-2 border-b border-border px-4 text-[13.5px]">
          <BookOpen size={15} className="text-fg-muted" />
          <span className="truncate font-medium">{q.data ? `«${q.data.card.title}» · v${q.data.card.version}` : "Карточка"}</span>
          {q.data && <span className="text-[12px] text-fg-muted">поисковая копия {q.data.card.search}</span>}
          <button type="button" onClick={onClose} className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover">
            <X size={16} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 text-[13.5px]">
          {q.isError && <p className="text-err">{q.error.message}</p>}
          {q.data && (
            <>
              <p className="whitespace-pre-wrap">{q.data.card.body}</p>
              <h3 className="text-[12px] font-medium uppercase tracking-wide text-fg-faint">Основания изменений</h3>
              {q.data.versions.map((v) => (
                <div key={v.number} className="rounded-[10px] border border-border px-3 py-2">
                  <div className="text-[12.5px] text-fg-muted">
                    <b className="text-fg">v{v.number}</b> · {when(v.date)} · {v.reason}
                  </div>
                  {v.remarks.length > 0 && <div className="mt-1 text-[12.5px]">Создана по {v.remarks.length} замечаниям:</div>}
                  {v.confirmed.length > 0 && <div className="mt-1 text-[12.5px]">Правило подтверждено {v.confirmed.length} замечаниями:</div>}
                  <div className="mt-1 flex flex-col gap-1.5">
                    {[...v.remarks, ...v.confirmed].map((r) => (
                      <RemarkCard key={r.id} r={r} invalidate={["qc-card", id]} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
